import { HttpStatusCode } from 'axios';
// @ts-ignore - interCeptor.js has no type declarations
import apiFetcher from '@/utils/interCeptor';
import type {
    CompleteAdminSetupPayload,
    CompleteEmployeeSetupPayload,
    CompleteEmployeeSetupResponse,
    MsgResponse,
    StoreEmployeeInviteReqBody,
    SetStoreLoginEmailRequestOtpPayload,
    SetStoreLoginEmailVerifyOtpPayload,
    StorePasswordResetConfirmPayload,
    StorePasswordResetRequestOtpPayload,
    StorePasswordResetVerifyOtpPayload,
    StoreAuthLoginPayload,
    StoreAuthLoginResponse,
    SetupValidateTokenResponse,
} from './authV3.types';

const setupValidateToken = async (token: string): Promise<SetupValidateTokenResponse> => {
    const response = await apiFetcher.get(`/api/v3/setup/validate-token?token=${encodeURIComponent(token)}`);
    if (response.status !== HttpStatusCode.Ok) {
        throw new Error('Failed to validate setup token');
    }
    return response.data as SetupValidateTokenResponse;
};

export const validateOutletSetupTokenApi = async (token: string): Promise<SetupValidateTokenResponse> => {
    return setupValidateToken(token);
};

export const validateEmployeeSetupTokenApi = async (token: string): Promise<SetupValidateTokenResponse> => {
    return setupValidateToken(token);
};

export const completeAdminSetupApi = async (
    payload: CompleteAdminSetupPayload,
): Promise<SetupValidateTokenResponse> => {
    const response = await apiFetcher.post('/api/v3/setup/admin/complete', payload);
    if (response.status !== HttpStatusCode.Ok) {
        throw new Error('Failed to complete admin setup');
    }
    return response.data as SetupValidateTokenResponse;
};

export const completeEmployeeSetupApi = async (
    payload: CompleteEmployeeSetupPayload,
): Promise<CompleteEmployeeSetupResponse> => {
    const response = await apiFetcher.post('/api/v3/setup/employee/complete', payload);
    if (response.status !== HttpStatusCode.Ok) {
        throw new Error('Failed to complete employee setup');
    }
    return response.data as CompleteEmployeeSetupResponse;
};

export const storeAuthLoginV3Api = async (payload: StoreAuthLoginPayload): Promise<StoreAuthLoginResponse> => {
    const response = await apiFetcher.post('/api/v3/store/auth/login', payload);
    if (response.status !== HttpStatusCode.Ok) {
        const err = new Error('Failed to login') as Error & { response: typeof response };
        err.response = response;
        throw err;
    }
    return response.data as StoreAuthLoginResponse;
};

export const requestStorePasswordResetOtpV3Api = async (
    payload: StorePasswordResetRequestOtpPayload,
): Promise<MsgResponse> => {
    const response = await apiFetcher.post('/api/v3/store/auth/password-reset/request-otp', payload);
    if (response.status !== HttpStatusCode.Ok) {
        throw new Error('Failed to request OTP');
    }
    return response.data as MsgResponse;
};

export const verifyStorePasswordResetOtpV3Api = async (
    payload: StorePasswordResetVerifyOtpPayload,
): Promise<MsgResponse> => {
    const response = await apiFetcher.post('/api/v3/store/auth/password-reset/verify-otp', payload);
    if (response.status !== HttpStatusCode.Ok) {
        throw new Error('Failed to verify OTP');
    }
    return response.data as MsgResponse;
};

export const confirmStorePasswordResetV3Api = async (
    payload: StorePasswordResetConfirmPayload,
): Promise<MsgResponse> => {
    const response = await apiFetcher.post('/api/v3/store/auth/password-reset/confirm', payload);
    if (response.status !== HttpStatusCode.Ok) {
        throw new Error('Failed to reset password');
    }
    return response.data as MsgResponse;
};

export const requestSetStoreLoginEmailOtpV3Api = async (
    payload: SetStoreLoginEmailRequestOtpPayload,
): Promise<MsgResponse> => {
    const response = await apiFetcher.post('/api/v3/store/auth/set-login-email/request-otp', payload);
    if (response.status !== HttpStatusCode.Ok) {
        const err = new Error('Failed to request login email OTP') as Error & { response: typeof response };
        err.response = response;
        throw err;
    }
    return response.data as MsgResponse;
};

/** Step 1: create employee row (if applicable) and send setup email; token uses employee_id. Uses store auth (CurrentOutlet). JSON body. */
export const inviteStoreEmployeeV3Api = async (payload: StoreEmployeeInviteReqBody): Promise<MsgResponse> => {
    const response = await apiFetcher.post('/api/v3/store/employees/invite', payload);
    if (response.status !== HttpStatusCode.Ok && response.status !== HttpStatusCode.Created) {
        const err = new Error('Failed to invite employee') as Error & { response: typeof response };
        err.response = response;
        throw err;
    }
    return response.data as MsgResponse;
};

/** Resend setup email for a pending store employee invitation. Uses store auth (CurrentOutlet). */
export const resendStoreEmployeeInviteV3Api = async (invitationId: string): Promise<MsgResponse> => {
    const response = await apiFetcher.post(
        `/api/v3/store/employees/invitations/${encodeURIComponent(invitationId)}/resend-invite`,
    );
    if (response.status !== HttpStatusCode.Ok && response.status !== HttpStatusCode.Created) {
        const err = new Error('Failed to resend employee invitation') as Error & { response: typeof response };
        err.response = response;
        throw err;
    }
    return response.data as MsgResponse;
};

export const verifySetStoreLoginEmailOtpV3Api = async (
    payload: SetStoreLoginEmailVerifyOtpPayload,
): Promise<MsgResponse> => {
    const response = await apiFetcher.post('/api/v3/store/auth/set-login-email/verify-otp', payload);
    if (response.status !== HttpStatusCode.Ok) {
        const err = new Error('Failed to verify login email OTP') as Error & { response: typeof response };
        err.response = response;
        throw err;
    }
    return response.data as MsgResponse;
};
